import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

const HIGHLIGHTS_CAROUSEL_SELECTOR = '[data-report-highlights-carousel]';
const HIGHLIGHTS_ITEM_SELECTOR = '[data-report-highlights-item]';
const SECTION_NAME = 'highlights';

const getTotalItemsCount = (carousel: HTMLElement): number => {
  const fromDataset = Number(carousel.dataset.reportTotalItemsCount);
  if (Number.isFinite(fromDataset) && fromDataset > 0) return fromDataset;
  return carousel.querySelectorAll(HIGHLIGHTS_ITEM_SELECTOR).length;
};

const buildProperties = (
  reportId: string,
  carousel: HTMLElement
): Record<string, unknown> => ({
  report_id: reportId,
  schema_version: POSTHOG_SCHEMA_VERSION,
  section_name: SECTION_NAME,
  total_items_count: getTotalItemsCount(carousel),
});

export const createHighlightsBarScrollTracker = (
  reportId: string,
  totalItemsCount: number
) => {
  let started = false;
  let completed = false;

  const properties = () => ({
    report_id: reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
    section_name: SECTION_NAME,
    total_items_count: totalItemsCount,
  });

  return {
    onScrollStart: () => {
      if (started || totalItemsCount < 2) return;
      started = trackEvent('high_hor_horizontal_scroll_started', properties());
    },
    onScrollComplete: () => {
      if (!started || completed) return;
      completed = trackEvent('high_hor_horizontal_scroll_completed', properties());
    },
  };
};

export const attachHighlightsCarouselTracking = (
  reportId: string,
  carousel: HTMLElement
): (() => void) => {
  let started = false;
  let completed = false;
  let lastVisible = false;
  let lastItemObserver: IntersectionObserver | undefined;

  const tryComplete = () => {
    if (!started || completed || !lastVisible) return;
    completed = trackEvent(
      'high_hor_horizontal_scroll_completed',
      buildProperties(reportId, carousel)
    );
    if (completed) {
      lastItemObserver?.disconnect();
    }
  };

  const handleScroll = () => {
    if (started || Math.abs(carousel.scrollLeft) < 1) return;
    started = trackEvent(
      'high_hor_horizontal_scroll_started',
      buildProperties(reportId, carousel)
    );
    if (!started) return;
    carousel.removeEventListener('scroll', handleScroll);
    carousel.removeEventListener('wheel', handleWheel);
    tryComplete();
  };

  const handleWheel = (event: WheelEvent) => {
    if (started) return;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    if (carousel.scrollWidth <= carousel.clientWidth) return;
    started = trackEvent(
      'high_hor_horizontal_scroll_started',
      buildProperties(reportId, carousel)
    );
    if (!started) return;
    carousel.removeEventListener('scroll', handleScroll);
    carousel.removeEventListener('wheel', handleWheel);
    tryComplete();
  };

  carousel.addEventListener('scroll', handleScroll, { passive: true });
  carousel.addEventListener('wheel', handleWheel, { passive: true });

  const lastItem = Array.from(
    carousel.querySelectorAll<HTMLElement>(HIGHLIGHTS_ITEM_SELECTOR)
  ).at(-1);

  if (lastItem) {
    lastItemObserver = new IntersectionObserver(
      (entries) => {
        lastVisible = entries.some((entry) => entry.isIntersecting);
        tryComplete();
      },
      { root: carousel, threshold: 0.01 }
    );
    lastItemObserver.observe(lastItem);
  }

  return () => {
    carousel.removeEventListener('scroll', handleScroll);
    carousel.removeEventListener('wheel', handleWheel);
    lastItemObserver?.disconnect();
  };
};

const scan = (node: ParentNode, attach: (element: HTMLElement) => void) => {
  if (node instanceof HTMLElement && node.matches(HIGHLIGHTS_CAROUSEL_SELECTOR)) {
    attach(node);
  }
  node.querySelectorAll?.(HIGHLIGHTS_CAROUSEL_SELECTOR).forEach((element) => {
    attach(element as HTMLElement);
  });
};

export const startHighlightsHorizontalScrollTracking = (
  reportId: string,
  root: ParentNode = document
): (() => void) => {
  if (
    !reportId ||
    typeof window === 'undefined' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => undefined;
  }

  const bound = new Set<HTMLElement>();
  const cleanups: Array<() => void> = [];

  const attach = (element: HTMLElement) => {
    if (bound.has(element)) return;
    bound.add(element);
    cleanups.push(attachHighlightsCarouselTracking(reportId, element));
  };

  scan(root, attach);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          scan(node, attach);
        }
      });
    });
  });

  const observeTarget =
    root instanceof Document ? root.documentElement : (root as Element);
  mutationObserver.observe(observeTarget, { childList: true, subtree: true });

  return () => {
    mutationObserver.disconnect();
    cleanups.forEach((stop) => stop());
  };
};
